import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Slider } from '../../components/ui/slider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, Users, Clock, FileText, MessageCircle, 
  Play, CheckCircle, AlertCircle, Star, Calendar,
  LogOut, Settings, Bell, ChevronRight, Send,
  Award, TrendingUp, Target, Pencil, Save, X,
  Eraser, Circle, Square, Type, Undo, Redo,
  ZoomIn, ZoomOut, Download, ChevronLeft, Palette,
  Eye, Image as ImageIcon, Video, SkipBack, SkipForward,
  Mail, Phone, GraduationCap, Maximize2, User, MapPin,
  Wallet, IndianRupee, History, Building, CreditCard, Receipt,
  Briefcase, Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// File size limits
const FILE_LIMITS = {
  image: {
    maxSize: 5 * 1024 * 1024, // 5MB
    formats: ['image/jpeg', 'image/png', 'image/webp'],
    dimensions: {
      'A4': { width: 2480, height: 3508 },
      'Half Imperial': { width: 2756, height: 3898 },
      '1024x1024': { width: 1024, height: 1024 }
    }
  },
  video: {
    maxSize: 10 * 1024 * 1024, // 10MB
    formats: ['video/mp4']
  }
};

// Full View Modal Component
const FullViewModal = ({ submission, onClose }) => {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Maximize2 className="w-5 h-5" />
            {submission?.assignment} - Full View
          </DialogTitle>
          <DialogDescription>
            Submitted by {submission?.student}
          </DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-[70vh] bg-black flex items-center justify-center">
          {submission?.type === 'video' ? (
            <video 
              src={submission?.fileUrl || submission?.thumbnail}
              controls
              className="max-w-full max-h-full"
            >
              Your browser does not support video playback.
            </video>
          ) : (
            <img 
              src={submission?.thumbnail}
              alt={submission?.assignment}
              className="max-w-full max-h-full object-contain"
            />
          )}
        </div>
        <DialogFooter className="p-4 border-t">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => window.open(submission?.thumbnail, '_blank')}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Student Detail Modal Component
const StudentDetailModal = ({ student, onClose }) => {
  if (!student) return null;
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Student Profile</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Student Header */}
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20">
              <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-2xl">
                {student.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-bold">{student.name}</h2>
              <p className="text-muted-foreground">{student.course}</p>
              <Badge variant="outline" className="mt-1">{student.batch || 'Morning Batch'}</Badge>
            </div>
          </div>
          
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Course Progress</span>
              <span className="font-bold">{student.progress}%</span>
            </div>
            <Progress value={student.progress} className="h-2" />
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-xl bg-muted">
              <p className="text-2xl font-bold text-emerald-500">{student.assignmentsCompleted || 8}</p>
              <p className="text-xs text-muted-foreground">Assignments</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-muted">
              <p className="text-2xl font-bold text-blue-500">{student.avgScore || 85}%</p>
              <p className="text-xs text-muted-foreground">Avg Score</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-muted">
              <p className="text-2xl font-bold text-purple-500">{student.attendance || 92}%</p>
              <p className="text-xs text-muted-foreground">Attendance</p>
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="space-y-2">
            <h3 className="font-semibold">Contact Information</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="w-4 h-4" />
              {student.email || `${student.name.toLowerCase().replace(' ', '.')}@student.via.edu`}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-4 h-4" />
              {student.phone || '+91 98765 43210'}
            </div>
          </div>
          
          {/* Recent Submissions */}
          <div className="space-y-2">
            <h3 className="font-semibold">Recent Submissions</h3>
            <div className="space-y-2">
              {(student.recentSubmissions || [
                { title: 'Color Theory Exercise', status: 'reviewed', score: 88 },
                { title: 'Typography Poster', status: 'pending' }
              ]).map((sub, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                  <span className="text-sm">{sub.title}</span>
                  {sub.status === 'reviewed' ? (
                    <Badge variant="outline" className="text-emerald-500">{sub.score}/100</Badge>
                  ) : (
                    <Badge variant="outline" className="text-orange-500">Pending</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button>
            <MessageCircle className="w-4 h-4 mr-2" />
            Send Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Annotation Preview Viewer - Shows mentor what annotations they submitted
const AnnotationPreviewViewer = ({ submission, onClose }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [displayedDimensions, setDisplayedDimensions] = useState({ width: 800, height: 600 });
  const imgRef = useRef(null);

  // Get the canvas size that was used when drawing annotations
  const canvasSize = submission?.canvasSize || { width: 800, height: 600 };
  const annotations = submission?.annotations || [];

  const handleImageLoad = (e) => {
    const img = e.target;
    setDisplayedDimensions({
      width: img.offsetWidth,
      height: img.offsetHeight
    });
    setImageLoaded(true);
  };

  // Calculate scale factors using the saved canvas size
  const scaleX = displayedDimensions.width / canvasSize.width;
  const scaleY = displayedDimensions.height / canvasSize.height;

  // Render annotation based on its type
  const renderAnnotation = (annotation, index) => {
    const color = annotation.color || '#ff0000';
    const lineWidth = (annotation.lineWidth || annotation.brushSize || 3) * Math.min(scaleX, scaleY);

    // Path/Pen annotations
    if ((annotation.type === 'path' || annotation.type === 'pen') && annotation.points && annotation.points.length > 0) {
      const pathData = annotation.points.map((p, i) => 
        `${i === 0 ? 'M' : 'L'} ${p.x * scaleX} ${p.y * scaleY}`
      ).join(' ');
      return (
        <path
          key={index}
          d={pathData}
          stroke={color}
          strokeWidth={lineWidth}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    }

    // Circle annotations
    if (annotation.type === 'circle') {
      const cx = (annotation.x || annotation.startX || 0) * scaleX;
      const cy = (annotation.y || annotation.startY || 0) * scaleY;
      const r = (annotation.radius || 30) * Math.min(scaleX, scaleY);
      return (
        <circle
          key={index}
          cx={cx}
          cy={cy}
          r={r}
          stroke={color}
          strokeWidth={lineWidth}
          fill="none"
        />
      );
    }

    // Rectangle annotations
    if (annotation.type === 'rectangle' || annotation.type === 'rect') {
      return (
        <rect
          key={index}
          x={(annotation.x || 0) * scaleX}
          y={(annotation.y || 0) * scaleY}
          width={(annotation.width || 50) * scaleX}
          height={(annotation.height || 50) * scaleY}
          stroke={color}
          strokeWidth={lineWidth}
          fill="none"
        />
      );
    }

    // Text annotations
    if (annotation.type === 'text') {
      const fontSize = (annotation.fontSize || 16) * Math.min(scaleX, scaleY);
      return (
        <text
          key={index}
          x={(annotation.x || 0) * scaleX}
          y={(annotation.y || 0) * scaleY}
          fill={color}
          fontSize={fontSize}
          fontFamily="Arial"
          fontWeight="bold"
        >
          {annotation.text || ''}
        </text>
      );
    }

    return null;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Your Annotations - {submission?.assignment}
          </DialogTitle>
          <DialogDescription>
            Score: {submission?.score}/100 • {annotations.length} annotations
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center bg-black/90 rounded-lg overflow-hidden p-4">
          <div className="relative inline-block">
            <img
              ref={imgRef}
              src={submission?.thumbnail}
              alt="Submission"
              className="max-w-full max-h-[60vh] object-contain"
              onLoad={handleImageLoad}
            />
            {/* SVG Overlay for annotations */}
            {imageLoaded && annotations.length > 0 && (
              <svg
                className="absolute top-0 left-0 pointer-events-none"
                width={displayedDimensions.width}
                height={displayedDimensions.height}
                style={{ width: displayedDimensions.width, height: displayedDimensions.height }}
              >
                {annotations.map((annotation, index) => renderAnnotation(annotation, index))}
              </svg>
            )}
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Pencil className="w-4 h-4" />
            {annotations.length} annotation{annotations.length !== 1 ? 's' : ''} • Canvas: {canvasSize.width}×{canvasSize.height}
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Mock data for mentor profile
const MOCK_MENTOR_PROFILE = {
  personal: {
    phone: '+91 98765 12345',
    address: '456, Artist Lane, Bangalore, Karnataka 560001',
    joiningDate: '2020-06-15'
  },
  career: {
    current: {
      designation: 'Senior Design Mentor',
      department: 'Design Foundation',
      experience: '4+ years at VIA',
      specializations: ['Typography', 'Color Theory', 'UI/UX Design', 'Brand Identity']
    },
    past: [
      { role: 'Lead Designer', company: 'Creative Studio Mumbai', duration: '2018-2020', description: 'Led design team of 8 people' },
      { role: 'UI/UX Designer', company: 'TechStart Solutions', duration: '2015-2018', description: 'Designed 50+ web applications' },
      { role: 'Graphic Designer', company: 'AdWorld Agency', duration: '2013-2015', description: 'Created campaigns for major brands' }
    ],
    education: [
      { degree: 'M.Des in Visual Communication', institution: 'NID Ahmedabad', year: '2013' },
      { degree: 'B.Des in Graphic Design', institution: 'MIT Institute of Design', year: '2011' }
    ]
  },
  earnings: {
    totalEarnings: 850000,
    currentMonth: 75000,
    pendingPayout: 25000,
    nextPayoutDate: '2025-02-01',
    studentsAssigned: 24,
    reviewsCompleted: 156,
    transactions: [
      { id: 'PAY001', date: '2025-01-01', amount: 75000, type: 'Monthly Salary', status: 'Paid', method: 'Bank Transfer' },
      { id: 'PAY002', date: '2024-12-01', amount: 75000, type: 'Monthly Salary', status: 'Paid', method: 'Bank Transfer' },
      { id: 'PAY003', date: '2024-11-01', amount: 70000, type: 'Monthly Salary', status: 'Paid', method: 'Bank Transfer' },
      { id: 'PAY004', date: '2024-10-15', amount: 15000, type: 'Workshop Bonus', status: 'Paid', method: 'Bank Transfer' },
      { id: 'PAY005', date: '2025-02-01', amount: 25000, type: 'Pending Reviews', status: 'Pending', method: '-' }
    ]
  }
};

// Mentor Profile Modal Component
const MentorProfileModal = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const profile = MOCK_MENTOR_PROFILE;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'M'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-xl font-bold">{user?.name || 'Mentor'}</p>
              <p className="text-sm text-muted-foreground font-normal">{profile.career.current.designation}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="personal" className="flex items-center gap-2">
              <User className="w-4 h-4" /> Personal
            </TabsTrigger>
            <TabsTrigger value="career" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> Career
            </TabsTrigger>
            <TabsTrigger value="earnings" className="flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Earnings
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[55vh] mt-4">
            {/* Personal Info Tab */}
            <TabsContent value="personal" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-500" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Full Name</p>
                      <p className="font-medium">{user?.name || 'Mentor Name'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        {user?.email}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        {profile.personal.phone}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Joining Date</p>
                      <p className="font-medium">{new Date(profile.personal.joiningDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      {profile.personal.address}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
                  <CardContent className="p-4 text-center">
                    <Users className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                    <p className="text-2xl font-bold">{profile.earnings.studentsAssigned}</p>
                    <p className="text-xs text-muted-foreground">Students Assigned</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold">{profile.earnings.reviewsCompleted}</p>
                    <p className="text-xs text-muted-foreground">Reviews Done</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/20">
                  <CardContent className="p-4 text-center">
                    <Trophy className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                    <p className="text-2xl font-bold">{profile.career.current.experience}</p>
                    <p className="text-xs text-muted-foreground">Experience</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Career Tab */}
            <TabsContent value="career" className="space-y-4">
              {/* Current Role */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-500" />
                    Current Role
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-lg">{profile.career.current.designation}</h3>
                        <p className="text-sm text-muted-foreground">{profile.career.current.department}</p>
                      </div>
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-600">
                        {profile.career.current.experience}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {profile.career.current.specializations.map((spec, idx) => (
                        <Badge key={idx} variant="secondary">{spec}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Education */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-emerald-500" />
                    Education
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {profile.career.education.map((edu, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 border rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{edu.degree}</h4>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Building className="w-3 h-3" /> {edu.institution}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Year: {edu.year}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Past Experience */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-orange-500" />
                    Past Experience
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {profile.career.past.map((exp, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 border rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-orange-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{exp.role}</h4>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Building className="w-3 h-3" /> {exp.company}
                        </p>
                        <p className="text-xs text-muted-foreground">{exp.duration}</p>
                        <p className="text-sm mt-1">{exp.description}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Earnings Tab */}
            <TabsContent value="earnings" className="space-y-4">
              {/* Earnings Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/20">
                  <CardContent className="p-4 text-center">
                    <IndianRupee className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                    <p className="text-2xl font-bold">₹{profile.earnings.totalEarnings.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-muted-foreground">Total Earnings</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
                  <CardContent className="p-4 text-center">
                    <Wallet className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold text-blue-500">₹{profile.earnings.currentMonth.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-muted-foreground">This Month</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border-orange-500/20">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                    <p className="text-2xl font-bold text-orange-500">₹{profile.earnings.pendingPayout.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </CardContent>
                </Card>
              </div>

              {/* Next Payout */}
              {profile.earnings.pendingPayout > 0 && (
                <Card className="border-emerald-500/30 bg-emerald-500/5">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="font-medium">Next Payout</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(profile.earnings.nextPayoutDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-500">₹{profile.earnings.pendingPayout.toLocaleString('en-IN')}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payment History */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-purple-500" />
                    Payment History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {profile.earnings.transactions.map((txn) => (
                      <div key={txn.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            txn.status === 'Paid' ? 'bg-emerald-500/20 text-emerald-500' :
                            txn.status === 'Pending' ? 'bg-orange-500/20 text-orange-500' :
                            'bg-gray-500/20 text-gray-500'
                          }`}>
                            {txn.status === 'Paid' ? <CheckCircle className="w-4 h-4" /> :
                             txn.status === 'Pending' ? <Clock className="w-4 h-4" /> :
                             <Calendar className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{txn.type}</p>
                            <p className="text-xs text-muted-foreground">{new Date(txn.date).toLocaleDateString('en-IN')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${txn.status === 'Paid' ? 'text-emerald-500' : ''}`}>
                            +₹{txn.amount.toLocaleString('en-IN')}
                          </p>
                          <Badge variant={txn.status === 'Paid' ? 'default' : 'secondary'} className="text-xs">
                            {txn.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button>
            <Settings className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Improved Annotation Tool Component with precise cursor snapping
const AnnotationTool = ({ submission, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const imageCanvasRef = useRef(null); // Separate canvas for background image
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#FF0000');
  const [brushSize, setBrushSize] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isVideo] = useState(submission?.type === 'video');
  const [comment, setComment] = useState('');
  const [score, setScore] = useState(''); // UNIFIED score input
  const [isSending, setIsSending] = useState(false); // Prevent double send
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  
  const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFFFFF', '#000000'];

  // Load image when component mounts
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      const maxWidth = 800;
      const maxHeight = 500;
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
      
      setCanvasSize({ width: Math.round(width), height: Math.round(height) });
      setImageLoaded(true);
    };
    img.onerror = () => {
      setCanvasSize({ width: 800, height: 500 });
      setImageLoaded(true);
    };
    img.src = submission?.thumbnail || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800';
  }, [submission]);

  // Redraw canvas whenever paths change or image loads
  useEffect(() => {
    if (!imageLoaded) return;
    redrawCanvas();
  }, [imageLoaded, canvasSize, paths]);

  // Redraw entire canvas (image + all annotations)
  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background image
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Image Preview', canvas.width / 2, canvas.height / 2);
    }
    
    // Draw all saved paths (excluding erased paths)
    paths.forEach(path => {
      if (path.type !== 'eraser') {
        drawPath(ctx, path);
      }
    });
  };

  // Draw a single path
  const drawPath = (ctx, path) => {
    ctx.save();
    ctx.strokeStyle = path.color;
    ctx.fillStyle = path.color;
    ctx.lineWidth = path.brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    if (path.type === 'pen') {
      if (path.points && path.points.length > 1) {
        ctx.beginPath();
        ctx.moveTo(path.points[0].x, path.points[0].y);
        for (let i = 1; i < path.points.length; i++) {
          ctx.lineTo(path.points[i].x, path.points[i].y);
        }
        ctx.stroke();
      }
    } else if (path.type === 'circle') {
      const radius = Math.sqrt(
        Math.pow(path.endX - path.startX, 2) + Math.pow(path.endY - path.startY, 2)
      );
      ctx.beginPath();
      ctx.arc(path.startX, path.startY, radius, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (path.type === 'rect') {
      ctx.strokeRect(path.startX, path.startY, path.endX - path.startX, path.endY - path.startY);
    } else if (path.type === 'text') {
      ctx.font = `${path.fontSize || 16}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(path.text, path.x, path.y);
    }
    ctx.restore();
  };

  // Get PRECISE canvas coordinates from mouse event - accounting for zoom and scroll
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    // Account for zoom transformation
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Mouse down handler
  const handleMouseDown = (e) => {
    e.preventDefault();
    const coords = getCoords(e);
    setIsDrawing(true);
    setStartPoint(coords);
    
    if (tool === 'text') {
      setTextPosition(coords);
      return;
    }
    
    if (tool === 'pen') {
      setCurrentPath([coords]);
    } else if (tool === 'eraser') {
      // For eraser, find and remove paths that intersect with click point
      eraseAtPoint(coords);
    }
  };

  // Mouse move handler
  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const coords = getCoords(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (tool === 'pen') {
      // Draw line segment directly for smooth drawing
      if (currentPath.length > 0) {
        const lastPoint = currentPath[currentPath.length - 1];
        ctx.strokeStyle = color;
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
      }
      setCurrentPath(prev => [...prev, coords]);
    } else if (tool === 'eraser') {
      // Continuously erase as mouse moves
      eraseAtPoint(coords);
    } else if (tool === 'circle' || tool === 'rect') {
      // Redraw with live shape preview
      redrawCanvas();
      
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      
      if (tool === 'circle') {
        const radius = Math.sqrt(
          Math.pow(coords.x - startPoint.x, 2) + Math.pow(coords.y - startPoint.y, 2)
        );
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (tool === 'rect') {
        ctx.strokeRect(startPoint.x, startPoint.y, coords.x - startPoint.x, coords.y - startPoint.y);
      }
    }
  };

  // Mouse up handler
  const handleMouseUp = (e) => {
    if (!isDrawing) return;
    
    const coords = getCoords(e);
    setIsDrawing(false);
    
    if (tool === 'pen' && currentPath.length > 1) {
      const newPath = {
        type: 'pen',
        color,
        brushSize,
        points: [...currentPath]
      };
      setPaths(prev => [...prev, newPath]);
    } else if (tool === 'circle' || tool === 'rect') {
      const newPath = {
        type: tool,
        color,
        brushSize,
        startX: startPoint.x,
        startY: startPoint.y,
        endX: coords.x,
        endY: coords.y
      };
      setPaths(prev => [...prev, newPath]);
    }
    
    setCurrentPath([]);
    setStartPoint(null);
  };

  // Eraser function - removes annotations near the point
  const eraseAtPoint = (point) => {
    const eraserRadius = brushSize * 3;
    
    setPaths(prevPaths => {
      return prevPaths.filter(path => {
        if (path.type === 'pen') {
          // Check if any point in the path is within eraser radius
          return !path.points.some(p => 
            Math.sqrt(Math.pow(p.x - point.x, 2) + Math.pow(p.y - point.y, 2)) < eraserRadius
          );
        } else if (path.type === 'circle') {
          // Check if click is near the circle
          const radius = Math.sqrt(
            Math.pow(path.endX - path.startX, 2) + Math.pow(path.endY - path.startY, 2)
          );
          const distToCenter = Math.sqrt(
            Math.pow(point.x - path.startX, 2) + Math.pow(point.y - path.startY, 2)
          );
          return Math.abs(distToCenter - radius) > eraserRadius;
        } else if (path.type === 'rect') {
          // Check if click is near any edge of the rectangle
          const nearTop = Math.abs(point.y - path.startY) < eraserRadius && 
                          point.x >= Math.min(path.startX, path.endX) && 
                          point.x <= Math.max(path.startX, path.endX);
          const nearBottom = Math.abs(point.y - path.endY) < eraserRadius && 
                             point.x >= Math.min(path.startX, path.endX) && 
                             point.x <= Math.max(path.startX, path.endX);
          const nearLeft = Math.abs(point.x - path.startX) < eraserRadius && 
                           point.y >= Math.min(path.startY, path.endY) && 
                           point.y <= Math.max(path.startY, path.endY);
          const nearRight = Math.abs(point.x - path.endX) < eraserRadius && 
                            point.y >= Math.min(path.startY, path.endY) && 
                            point.y <= Math.max(path.startY, path.endY);
          return !(nearTop || nearBottom || nearLeft || nearRight);
        } else if (path.type === 'text') {
          // Approximate text bounding box
          const textWidth = path.text.length * (path.fontSize || 16) * 0.6;
          const textHeight = (path.fontSize || 16);
          return !(point.x >= path.x && point.x <= path.x + textWidth &&
                   point.y >= path.y - textHeight && point.y <= path.y);
        }
        return true;
      });
    });
  };

  // Undo function
  const handleUndo = () => {
    if (paths.length === 0) return;
    setPaths(prev => prev.slice(0, -1));
  };

  // Clear all annotations
  const handleClear = () => {
    setPaths([]);
  };

  // Add text at clicked position
  const addText = () => {
    if (!textInput || !textPosition) return;
    
    const textPath = {
      type: 'text',
      text: textInput,
      x: textPosition.x,
      y: textPosition.y,
      color,
      fontSize: Math.max(12, brushSize * 4)
    };
    
    setPaths(prev => [...prev, textPath]);
    setTextInput('');
    setTextPosition(null);
  };

  // Normalize annotations to a standard format for consistent display
  const normalizeAnnotations = (rawPaths) => {
    return rawPaths.map(path => {
      if (path.type === 'pen') {
        // Pen/path annotations - ensure consistent format
        return {
          type: 'path', // Standardize to 'path' for student viewer
          color: path.color,
          lineWidth: path.brushSize || path.lineWidth || 3,
          points: path.points
        };
      } else if (path.type === 'circle') {
        // Circle annotations - convert from startX/endX to centerX/centerY/radius
        const radius = Math.sqrt(
          Math.pow(path.endX - path.startX, 2) + Math.pow(path.endY - path.startY, 2)
        );
        return {
          type: 'circle',
          color: path.color,
          lineWidth: path.brushSize || path.lineWidth || 3,
          x: path.startX, // Center X
          y: path.startY, // Center Y
          radius: radius
        };
      } else if (path.type === 'rect') {
        // Rectangle annotations - convert to x,y,width,height format
        return {
          type: 'rectangle',
          color: path.color,
          lineWidth: path.brushSize || path.lineWidth || 3,
          x: Math.min(path.startX, path.endX),
          y: Math.min(path.startY, path.endY),
          width: Math.abs(path.endX - path.startX),
          height: Math.abs(path.endY - path.startY)
        };
      } else if (path.type === 'text') {
        // Text annotations - ensure consistent format
        return {
          type: 'text',
          color: path.color,
          fontSize: path.fontSize || 16,
          x: path.x,
          y: path.y,
          text: path.text
        };
      }
      // Return as-is for unknown types
      return path;
    });
  };

  // Save annotations
  const handleSave = () => {
    // Validate score
    if (!score || parseInt(score) < 0 || parseInt(score) > 100) {
      toast.error('Please enter a valid score between 0 and 100');
      return;
    }
    
    // Prevent double send
    if (isSending) {
      toast.warning('Sending in progress...');
      return;
    }
    
    setIsSending(true);
    
    // Normalize paths to standard format for consistent display on student side
    const normalizedPaths = normalizeAnnotations(paths);
    
    const annotationData = {
      submission_id: submission.id,
      paths: normalizedPaths, // Use normalized annotations
      score: parseInt(score),
      general_comment: comment,
      canvasSize: canvasSize, // Include canvas dimensions for proper scaling on student view
      created_at: new Date().toISOString()
    };
    
    onSave(annotationData);
    toast.success('Review saved and sent to student!');
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5" />
            Review Submission: {submission?.assignment}
          </DialogTitle>
          <DialogDescription>
            Draw, highlight, or add text comments directly on the student's work
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* Toolbar */}
          <div className="w-16 flex flex-col gap-2 p-2 bg-muted rounded-xl">
            <Button
              variant={tool === 'pen' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setTool('pen')}
              title="Pen - Freehand drawing"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant={tool === 'eraser' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setTool('eraser')}
              title="Eraser - Click on annotations to remove"
            >
              <Eraser className="w-4 h-4" />
            </Button>
            <Button
              variant={tool === 'circle' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setTool('circle')}
              title="Circle - Click and drag from center"
            >
              <Circle className="w-4 h-4" />
            </Button>
            <Button
              variant={tool === 'rect' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setTool('rect')}
              title="Rectangle - Click and drag"
            >
              <Square className="w-4 h-4" />
            </Button>
            <Button
              variant={tool === 'text' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setTool('text')}
              title="Text - Click to place text"
            >
              <Type className="w-4 h-4" />
            </Button>
            
            <div className="border-t border-border my-2" />
            
            <Button variant="ghost" size="icon" onClick={handleUndo} title="Undo last annotation">
              <Undo className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleClear} title="Clear all annotations">
              <X className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.min(z + 0.25, 2))} title="Zoom In">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} title="Zoom Out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            
            <div className="border-t border-border my-2" />
            
            {/* Color picker */}
            <div className="grid grid-cols-2 gap-1">
              {colors.map(c => (
                <button
                  key={c}
                  className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-primary ring-2 ring-primary/50' : 'border-gray-400'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  title={c}
                />
              ))}
            </div>
            
            {/* Brush size */}
            <div className="mt-2 text-xs text-center text-muted-foreground">
              Size: {brushSize}
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          
          {/* Canvas Area */}
          <div className="flex-1 flex flex-col">
            <div 
              ref={containerRef}
              className="flex-1 overflow-auto bg-muted/30 rounded-xl flex items-center justify-center p-4"
            >
              <canvas
                ref={canvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                className="border border-border rounded-lg shadow-lg"
                style={{ 
                  transform: `scale(${zoom})`, 
                  transformOrigin: 'center center',
                  cursor: tool === 'eraser' ? 'crosshair' : tool === 'text' ? 'text' : 'crosshair'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
            </div>
            
            {/* Video controls (if video submission) */}
            {isVideo && (
              <div className="flex items-center gap-4 mt-3 p-3 bg-muted rounded-xl">
                <Button variant="ghost" size="icon" onClick={() => setCurrentFrame(f => Math.max(0, f - 1))}>
                  <SkipBack className="w-4 h-4" />
                </Button>
                <div className="flex-1">
                  <Slider
                    value={[currentFrame]}
                    onValueChange={([v]) => setCurrentFrame(v)}
                    max={100}
                    step={1}
                  />
                </div>
                <Button variant="ghost" size="icon" onClick={() => setCurrentFrame(f => Math.min(100, f + 1))}>
                  <SkipForward className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground">Frame: {currentFrame}</span>
              </div>
            )}
            
            {/* Text input (when text tool is active and position is set) */}
            {textPosition && (
              <div className="flex gap-2 mt-3 p-3 bg-muted rounded-xl">
                <Input
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Type your annotation text..."
                  className="flex-1"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && addText()}
                />
                <Button onClick={addText}>Add</Button>
                <Button variant="ghost" onClick={() => setTextPosition(null)}>Cancel</Button>
              </div>
            )}
          </div>
          
          {/* Comment Panel */}
          <div className="w-72 flex flex-col gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Submission Info</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p><strong>Student:</strong> {submission?.student}</p>
                <p><strong>Assignment:</strong> {submission?.assignment}</p>
                <p><strong>Course:</strong> {submission?.course}</p>
                <p><strong>Submitted:</strong> {submission?.submittedAt}</p>
                <p><strong>Type:</strong> {submission?.type === 'video' ? 'Video (MP4)' : 'Image'}</p>
              </CardContent>
            </Card>
            
            {/* Accepted Formats */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Accepted Formats</CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1 text-muted-foreground">
                <p><strong>Images:</strong> A4, Half Imperial, 1024x1024</p>
                <p>Max size: 5MB (JPEG, PNG, WebP)</p>
                <p><strong>Video:</strong> MP4 only</p>
                <p>Max size: 10MB</p>
              </CardContent>
            </Card>
            
            {/* Score Input - UNIFIED */}
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Score (Required)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    placeholder="0-100"
                    className="w-24 text-center text-lg font-bold"
                  />
                  <span className="text-muted-foreground">/100</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="flex-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">General Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Write your overall feedback here..."
                  className="h-32 resize-none"
                />
              </CardContent>
            </Card>
            
            <div className="text-xs text-muted-foreground p-2 bg-muted/50 rounded-lg">
              <p><strong>Tips:</strong></p>
              <p>• Pen: Draw freehand annotations</p>
              <p>• Eraser: Click on annotations to remove</p>
              <p>• Circle/Rect: Click and drag to draw shapes</p>
              <p>• Text: Click to place, then type</p>
              {paths.length > 0 && (
                <p className="mt-2 text-primary font-medium">{paths.length} annotation(s) added</p>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            className="bg-gradient-to-r from-emerald-500 to-cyan-500"
            disabled={!score || isSending}
          >
            <Send className="w-4 h-4 mr-2" />
            {isSending ? 'Sending...' : 'Submit Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function MentorDashboard() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [courses, setCourses] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // Full conversation history
  const [schedule, setSchedule] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [annotatingSubmission, setAnnotatingSubmission] = useState(null);
  const [fullViewSubmission, setFullViewSubmission] = useState(null);
  const [viewingAnnotations, setViewingAnnotations] = useState(null); // For viewing submitted annotations
  const [showProfileModal, setShowProfileModal] = useState(false); // Profile modal state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedChatStudent, setSelectedChatStudent] = useState(null); // For chat
  const [critique, setCritique] = useState('');
  const [score, setScore] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollingRef = useRef(null);
  const submissionsPollingRef = useRef(null);

  // WebSocket URL
  const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

  // Load messages function for polling
  const loadMessagesForChat = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const messagesRes = await axios.get(`${API}/messages`, { headers, withCredentials: true });
      
      if (messagesRes.data && messagesRes.data.length > 0) {
        setAllMessages(messagesRes.data);
        
        // Group by conversation partner for inbox view
        const inboxMap = {};
        messagesRes.data.forEach(m => {
          let partnerId, partnerName;
          if (m.sender_id === user?.user_id) {
            partnerId = m.recipient_id;
            partnerName = m.recipient_name;
          } else {
            partnerId = m.sender_id;
            partnerName = m.sender_name;
          }
          
          if (!inboxMap[partnerId] || new Date(m.created_at) > new Date(inboxMap[partnerId].created_at)) {
            inboxMap[partnerId] = {
              id: m.message_id,
              from: partnerName,
              senderId: partnerId,
              message: m.content,
              time: formatTimeAgo(m.created_at),
              unread: m.recipient_id === user?.user_id && !m.read,
              created_at: m.created_at
            };
          }
        });
        setMessages(Object.values(inboxMap).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (err) {
      console.log('Error loading messages:', err);
    }
  }, [user?.user_id]);

  // Helper function to format time ago (declared early for use in loadMessagesForChat)
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Polling for real-time messages when chat is active
  useEffect(() => {
    if (activeTab === 'messages') {
      // Start polling every 2 seconds for real-time feel
      pollingRef.current = setInterval(loadMessagesForChat, 2000);
      loadMessagesForChat(); // Initial load
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeTab, loadMessagesForChat]);

  // WebSocket connection for real-time messaging (backup/enhancement)
  useEffect(() => {
    if (!user?.user_id) return;

    const connectWebSocket = () => {
      try {
        const wsUrl = `${WS_URL}/ws/user/${user.user_id}`;
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('Mentor WebSocket connected');
          const pingInterval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
          wsRef.current.pingInterval = pingInterval;
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            // Refresh messages when new one arrives
            loadMessagesForChat();
            toast.success(`New message from ${data.message.sender_name}`);
          }
        };

        wsRef.current.onclose = () => {
          console.log('Mentor WebSocket disconnected, using polling');
        };

        wsRef.current.onerror = (error) => {
          console.log('Mentor WebSocket unavailable, using polling');
        };
      } catch (error) {
        console.log('WebSocket connection failed, using polling');
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current?.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user?.user_id, WS_URL, loadMessagesForChat]);

  // Load submissions function for polling
  const loadSubmissionsForPolling = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const submissionsRes = await axios.get(`${API}/mentor/dashboard/submissions`, { headers, withCredentials: true });
      
      if (submissionsRes.data && submissionsRes.data.length > 0) {
        const formattedSubmissions = submissionsRes.data.map(sub => ({
          id: sub.submission_id,
          student: sub.student_name,
          studentId: sub.student_id,
          course: sub.course_name,
          assignment: sub.assignment_title,
          submittedAt: formatTimeAgo(sub.submitted_at),
          status: sub.status,
          score: sub.score,
          feedback: sub.feedback,
          thumbnail: sub.file_url,
          type: sub.file_type,
          annotations: sub.annotations || [],
          canvasSize: sub.canvas_size || { width: 800, height: 600 }, // Include canvas size for annotation viewing
          hasAnnotations: sub.annotations && sub.annotations.length > 0
        }));
        setSubmissions(formattedSubmissions);
      }
    } catch (err) {
      console.log('Error loading submissions:', err);
    }
  }, []);

  // Polling for real-time submissions when on submissions tab
  useEffect(() => {
    if (activeTab === 'submissions' || activeTab === 'overview') {
      // Start polling every 3 seconds for real-time submissions
      submissionsPollingRef.current = setInterval(loadSubmissionsForPolling, 3000);
      loadSubmissionsForPolling(); // Initial load
    }
    
    return () => {
      if (submissionsPollingRef.current) {
        clearInterval(submissionsPollingRef.current);
      }
    };
  }, [activeTab, loadSubmissionsForPolling]);

  // Auto-scroll chat
  useEffect(() => {
    if (activeTab === 'messages' && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages, activeTab, selectedChatStudent]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Load mentor stats
      const statsRes = await axios.get(`${API}/mentor/stats`, { headers, withCredentials: true }).catch(() => ({ data: {} }));
      setStats(statsRes.data || {});

      // Load real submissions from API
      try {
        const submissionsRes = await axios.get(`${API}/mentor/dashboard/submissions`, { headers, withCredentials: true });
        if (submissionsRes.data && submissionsRes.data.length > 0) {
          // Transform API data to frontend format
          const formattedSubmissions = submissionsRes.data.map(sub => ({
            id: sub.submission_id,
            student: sub.student_name,
            course: sub.course_name,
            assignment: sub.assignment_title,
            submittedAt: formatTimeAgo(sub.submitted_at),
            status: sub.status,
            score: sub.score,
            thumbnail: sub.file_url,
            type: sub.file_type,
            annotations: sub.annotations || [],
            canvasSize: sub.canvas_size || { width: 800, height: 600 },
            hasAnnotations: !!sub.annotations && sub.annotations.length > 0
          }));
          setSubmissions(formattedSubmissions);
        } else {
          // No submissions yet - show empty state
          setSubmissions([]);
        }
      } catch (err) {
        console.log('No submissions found');
        setSubmissions([]);
      }

      // Load real students from API
      try {
        const studentsRes = await axios.get(`${API}/mentor/dashboard/students`, { headers, withCredentials: true });
        if (studentsRes.data && studentsRes.data.length > 0) {
          const formattedStudents = studentsRes.data.map(s => ({
            id: s.user_id,
            name: s.name,
            email: s.email,
            course: s.enrolled_courses?.[0] || 'Design Foundation',
            progress: s.progress || 0,
            batch: 'Morning Batch',
            assignmentsCompleted: s.reviewed_submissions || 0,
            avgScore: s.avg_score || 0,
            attendance: 90,
            xp_points: s.xp_points || 0,
            badges: s.badges || []
          }));
          setStudents(formattedStudents);
        } else {
          // No students assigned yet
          setStudents([]);
        }
      } catch (err) {
        console.log('No students data');
        setStudents([]);
      }

      // Load courses from API
      try {
        const coursesRes = await axios.get(`${API}/mentor/dashboard/courses`, { headers, withCredentials: true });
        if (coursesRes.data && coursesRes.data.length > 0) {
          setCourses(coursesRes.data);
        } else {
          // No courses assigned yet
          setCourses([]);
        }
      } catch (err) {
        console.log('No courses data');
        setCourses([]);
      }

      // Load batches from API
      try {
        const batchesRes = await axios.get(`${API}/mentor/dashboard/batches`, { headers, withCredentials: true });
        if (batchesRes.data && batchesRes.data.length > 0) {
          setBatches(batchesRes.data);
        } else {
          // No batches yet
          setBatches([]);
        }
      } catch (err) {
        console.log('No batches data');
        setBatches([]);
      }

      // Load real messages from API
      try {
        const messagesRes = await axios.get(`${API}/messages`, { headers, withCredentials: true });
        if (messagesRes.data && messagesRes.data.length > 0) {
          // Store all messages for chat history
          setAllMessages(messagesRes.data);
          
          // Group by conversation partner for inbox view
          const inboxMap = {};
          messagesRes.data.forEach(m => {
            // Determine the conversation partner (not the current user)
            let partnerId, partnerName;
            if (m.sender_id === user?.user_id) {
              // Message sent by mentor - partner is recipient
              partnerId = m.recipient_id;
              partnerName = m.recipient_name;
            } else {
              // Message received by mentor - partner is sender
              partnerId = m.sender_id;
              partnerName = m.sender_name;
            }
            
            // Update or create inbox entry for this partner
            if (!inboxMap[partnerId] || new Date(m.created_at) > new Date(inboxMap[partnerId].created_at)) {
              inboxMap[partnerId] = {
                id: m.message_id,
                from: partnerName,
                senderId: partnerId,
                message: m.content,
                time: formatTimeAgo(m.created_at),
                unread: m.recipient_id === user?.user_id && !m.read,
                created_at: m.created_at
              };
            }
          });
          setMessages(Object.values(inboxMap).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        } else {
          setMessages([]);
          setAllMessages([]);
        }
      } catch (err) {
        console.log('Error loading messages:', err);
        setMessages([]);
        setAllMessages([]);
      }

      // Load schedule from API
      try {
        const scheduleRes = await axios.get(`${API}/mentor/dashboard/schedule`, { headers, withCredentials: true });
        if (scheduleRes.data && scheduleRes.data.length > 0) {
          setSchedule(scheduleRes.data);
        } else {
          // Fallback demo data
          setSchedule([
            { id: 'sch1', title: 'Design Foundation - Morning', time: '10:00 AM - 12:00 PM', day: 'Today', type: 'class' },
            { id: 'sch2', title: '3D Modeling - Core Training', time: '2:00 PM - 4:00 PM', day: 'Today', type: 'class' },
            { id: 'sch3', title: 'Assignment Review Session', time: '5:00 PM - 6:00 PM', day: 'Tomorrow', type: 'review' },
            { id: 'sch4', title: 'Design Foundation - Evening', time: '6:00 PM - 8:00 PM', day: 'Tomorrow', type: 'class' }
          ]);
        }
      } catch (err) {
        console.log('Using demo schedule data');
        setSchedule([
          { id: 'sch1', title: 'Design Foundation - Morning', time: '10:00 AM - 12:00 PM', day: 'Today', type: 'class' },
          { id: 'sch2', title: '3D Modeling - Core Training', time: '2:00 PM - 4:00 PM', day: 'Today', type: 'class' },
          { id: 'sch3', title: 'Assignment Review Session', time: '5:00 PM - 6:00 PM', day: 'Tomorrow', type: 'review' },
          { id: 'sch4', title: 'Design Foundation - Evening', time: '6:00 PM - 8:00 PM', day: 'Tomorrow', type: 'class' }
        ]);
      }

    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleGradeSubmission = async () => {
    if (!selectedSubmission || !score) {
      toast.error('Please enter a score');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/mentor/submissions/${selectedSubmission.id}/review`, {
        score: parseInt(score),
        feedback: critique,
        annotations: []
      }, { headers, withCredentials: true });
      
      toast.success(`Assignment graded: ${score}/100`);
      
      // Update local state
      setSubmissions(prev => prev.map(s => 
        s.id === selectedSubmission.id 
          ? { ...s, status: 'reviewed', score: parseInt(score) }
          : s
      ));
      
      setSelectedSubmission(null);
      setCritique('');
      setScore('');
    } catch (error) {
      console.error('Error grading submission:', error);
      toast.error('Failed to submit grade. Please try again.');
    }
  };

  const handleSaveAnnotation = async (annotationData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/mentor/submissions/${annotationData.submission_id}/review`, {
        score: annotationData.score, // Use score from annotationData
        feedback: annotationData.general_comment,
        annotations: annotationData.paths,
        canvasSize: annotationData.canvasSize // Include canvas dimensions for proper annotation scaling
      }, { headers, withCredentials: true });
      
      // Update submission status locally
      setSubmissions(prev => prev.map(s => 
        s.id === annotationData.submission_id 
          ? { ...s, status: 'reviewed', score: annotationData.score, hasAnnotations: true }
          : s
      ));
      
      toast.success('Review with annotations saved and notification sent to student!');
      setAnnotatingSubmission(null);
    } catch (error) {
      console.error('Error saving annotation:', error);
      toast.error('Failed to save review. Please try again.');
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !replyingTo) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/messages`, {
        recipient_id: replyingTo.senderId,
        content: replyText
      }, { headers, withCredentials: true });
      
      // Add to local messages immediately
      if (response.data.data) {
        setAllMessages(prev => [...prev, response.data.data]);
      }
      
      toast.success(`Message sent to ${replyingTo.from}!`);
      setReplyingTo(null);
      setReplyText('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  // Send chat message function
  const handleSendChatMessage = async () => {
    if (!chatMessage.trim() || !selectedChatStudent) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/messages`, {
        recipient_id: selectedChatStudent.senderId,
        content: chatMessage
      }, { headers, withCredentials: true });
      
      // Add to local messages immediately
      if (response.data.data) {
        setAllMessages(prev => [...prev, response.data.data]);
      }
      
      setChatMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  // Get conversation with specific student
  const getConversationWithStudent = (studentId) => {
    return allMessages
      .filter(m => 
        (m.sender_id === studentId && m.recipient_id === user?.user_id) ||
        (m.sender_id === user?.user_id && m.recipient_id === studentId)
      )
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const cardClass = theme === 'dark' ? '' : 'shadow-lg';

  // Sidebar navigation items
  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: Target },
    { id: 'courses', label: 'My Courses', icon: BookOpen },
    { id: 'batches', label: 'My Batches', icon: Users },
    { id: 'submissions', label: 'Submissions', icon: FileText },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'messages', label: 'Messages', icon: MessageCircle, badge: messages.filter(m => m.unread).length },
    { id: 'schedule', label: 'Schedule', icon: Calendar }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="mentor-dashboard">
      {/* Top Header Bar */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_via-platform/artifacts/epc7ik46_VIA_LOGO_Tranparent.png"
              alt="VIA Logo"
              className="w-14 h-14 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold">Mentor Dashboard</h1>
              <p className="text-sm text-muted-foreground">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="font-mono">
              {stats.vendor_id || 'VIA-MENT-XXXXXX'}
            </Badge>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
              {messages.filter(m => m.unread).length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {messages.filter(m => m.unread).length}
                </span>
              )}
            </Button>
            
            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 cursor-pointer ring-2 ring-purple-500/30 hover:ring-purple-500/60 transition-all">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                      {user?.name?.split(' ').map(n => n[0]).join('') || 'M'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name || 'Mentor'}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                  <Briefcase className="mr-2 h-4 w-4" />
                  <span>My Career</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                  <Wallet className="mr-2 h-4 w-4" />
                  <span>Earnings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] bg-card border-r border-border p-4 hidden lg:block">
          <nav className="space-y-2">
            {sidebarItems.map(item => (
              <Button
                key={item.id}
                variant={activeTab === item.id ? 'secondary' : 'ghost'}
                className="w-full justify-start"
                onClick={() => setActiveTab(item.id)}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.label}
                {item.badge > 0 && (
                  <Badge variant="destructive" className="ml-auto text-xs">
                    {item.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </nav>

          {/* Teaching Stats */}
          <div className="mt-8 p-4 rounded-xl bg-muted/50">
            <h3 className="font-semibold mb-3">Teaching Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Hours Taught</span>
                <span className="font-bold">{stats.total_teaching_hours || 0}h</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Modules Completed</span>
                <span className="font-bold">{stats.modules_completed || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">On-Time Rate</span>
                <span className="font-bold text-emerald-500">{stats.on_time_percentage || 100}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Students Taught</span>
                <span className="font-bold">{stats.total_students_taught || 0}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: BookOpen, label: 'Active Courses', value: courses.length, color: 'text-blue-500' },
                  { icon: Users, label: 'Total Students', value: batches.reduce((a, b) => a + b.students, 0), color: 'text-emerald-500' },
                  { icon: FileText, label: 'Pending Reviews', value: submissions.filter(s => s.status === 'pending').length, color: 'text-orange-500' },
                  { icon: Clock, label: 'Hours This Week', value: '12h', color: 'text-purple-500' }
                ].map((stat, i) => (
                  <Card key={i} className={cardClass}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-muted flex items-center justify-center ${stat.color}`}>
                        <stat.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pending Submissions */}
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-orange-500" />
                      Pending Reviews
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {submissions.filter(s => s.status === 'pending').slice(0, 3).map(sub => (
                        <div key={sub.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab('submissions')}>
                          <img src={sub.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{sub.assignment}</p>
                            <p className="text-xs text-muted-foreground">{sub.student} • {sub.submittedAt}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('submissions')}>
                      View All Submissions
                    </Button>
                  </CardContent>
                </Card>

                {/* Upcoming Schedule */}
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Today's Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {schedule.filter(s => s.day === 'Today').map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                          <div className={`w-2 h-10 rounded-full ${item.type === 'class' ? 'bg-blue-500' : 'bg-orange-500'}`} />
                          <div className="flex-1">
                            <p className="font-medium">{item.title}</p>
                            <p className="text-xs text-muted-foreground">{item.time}</p>
                          </div>
                          <Button size="sm" variant="outline">
                            <Play className="w-3 h-3 mr-1" /> Start
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('schedule')}>
                      View Full Schedule
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">My Courses</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {courses.map((course) => (
                  <Card key={course.id} className={`${cardClass} overflow-hidden`}>
                    <div className="flex">
                      <img 
                        src={course.thumbnail} 
                        alt={course.title}
                        className="w-32 h-full object-cover"
                      />
                      <CardContent className="p-4 flex-1">
                        <Badge className="mb-2">{course.id}</Badge>
                        <h3 className="font-bold text-lg mb-1">{course.title}</h3>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {course.students} students
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Next: {course.nextClass}
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="flex justify-between text-xs mb-1">
                            <span>Progress</span>
                            <span>{course.completedModules}/{course.modules} modules</span>
                          </div>
                          <Progress value={(course.completedModules / course.modules) * 100} className="h-2" />
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" className="flex-1">
                            <Play className="w-4 h-4 mr-1" /> Start Class
                          </Button>
                          <Button size="sm" variant="outline">
                            View Content
                          </Button>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Submissions Tab */}
          {activeTab === 'submissions' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Student Submissions</h2>
                <Badge variant="outline">{submissions.filter(s => s.status === 'pending').length} pending</Badge>
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Submissions List */}
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle className="text-lg">Submissions Queue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-3">
                        {submissions.map((sub) => (
                          <div
                            key={sub.id}
                            onClick={() => setSelectedSubmission(sub)}
                            className={`p-3 rounded-xl border cursor-pointer transition-all ${
                              selectedSubmission?.id === sub.id 
                                ? 'border-primary bg-primary/5' 
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="relative">
                                <img 
                                  src={sub.thumbnail} 
                                  alt=""
                                  className="w-16 h-16 rounded-lg object-cover"
                                />
                                {sub.type === 'video' && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                                    <Video className="w-6 h-6 text-white" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium">{sub.student}</span>
                                  {sub.status === 'pending' ? (
                                    <Badge variant="outline" className="text-orange-500 border-orange-500">Pending</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-emerald-500 border-emerald-500">Reviewed</Badge>
                                  )}
                                  {sub.hasAnnotations && (
                                    <Badge variant="outline" className="text-purple-500 border-purple-500">
                                      <Pencil className="w-3 h-3 mr-1" />
                                      Annotated
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground truncate">{sub.assignment}</p>
                                <p className="text-xs text-muted-foreground">{sub.course} • {sub.submittedAt}</p>
                                {sub.score && (
                                  <p className="text-sm font-bold text-emerald-500 mt-1">Score: {sub.score}/100</p>
                                )}
                              </div>
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Review Panel */}
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle className="text-lg">Review & Critique</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedSubmission ? (
                      <div className="space-y-4">
                        <div className="relative aspect-video bg-muted rounded-xl flex items-center justify-center overflow-hidden">
                          <img 
                            src={selectedSubmission.thumbnail}
                            alt="Submission"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{selectedSubmission.assignment}</h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            By {selectedSubmission.student} • {selectedSubmission.course}
                          </p>
                          {selectedSubmission.status === 'reviewed' ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-emerald-500 border-emerald-500">
                                Reviewed
                              </Badge>
                              <span className="text-sm font-medium">Score: {selectedSubmission.score}/100</span>
                            </div>
                          ) : (
                            <Badge variant="secondary" className="text-orange-500 bg-orange-500/10">
                              Awaiting Review
                            </Badge>
                          )}
                        </div>
                        
                        {/* Info only - no duplicate form */}
                        <div className="text-sm text-muted-foreground border-t pt-4 space-y-2">
                          <p className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Submitted: {selectedSubmission.submittedAt}
                          </p>
                          {selectedSubmission.hasAnnotations && (
                            <p className="flex items-center gap-2 text-purple-500">
                              <Pencil className="w-4 h-4" />
                              Has visual annotations
                            </p>
                          )}
                        </div>
                        
                        {/* Action Buttons - Always Visible */}
                        <div className="flex flex-col gap-2 pt-2 border-t">
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => setAnnotatingSubmission(selectedSubmission)} 
                              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              {selectedSubmission.status === 'reviewed' ? 'Edit Review' : 'Review & Annotate'}
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => setFullViewSubmission(selectedSubmission)}
                            >
                              <Maximize2 className="w-4 h-4 mr-2" />
                              Full View
                            </Button>
                          </div>
                          {/* View My Annotations button - only show for reviewed submissions with annotations */}
                          {selectedSubmission.status === 'reviewed' && selectedSubmission.hasAnnotations && (
                            <Button 
                              variant="secondary"
                              onClick={() => setViewingAnnotations(selectedSubmission)}
                              className="w-full"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View My Annotations (What Student Sees)
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="h-[400px] flex items-center justify-center text-center">
                        <div>
                          <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                          <p className="text-muted-foreground">Select a submission to review</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Batches Tab */}
          {activeTab === 'batches' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">My Batches</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {batches.map((batch) => (
                  <Card key={batch.id} className={cardClass}>
                    <CardContent className="p-5">
                      <Badge className="mb-3">{batch.course}</Badge>
                      <h3 className="font-bold text-lg mb-2">{batch.name}</h3>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {batch.students} students
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {batch.schedule}
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Started: {batch.startDate}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" className="flex-1">View Students</Button>
                        <Button size="sm" variant="outline" className="flex-1">Class Notes</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">My Students</h2>
              <Card className={cardClass}>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {students.map((student) => (
                      <div 
                        key={student.id} 
                        className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/30 transition-all cursor-pointer"
                        onClick={() => setSelectedStudent(student)}
                        data-testid={`student-${student.id}`}
                      >
                        <Avatar>
                          <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white">
                            {student.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium hover:text-primary transition-colors">{student.name}</p>
                          <p className="text-xs text-muted-foreground">{student.course}</p>
                          <Progress value={student.progress} className="h-1 mt-1" />
                        </div>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toast.info('Chat feature coming soon!'); }}>
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Messages Tab - MODERN CHAT INTERFACE */}
          {activeTab === 'messages' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Messages</h2>
                <Badge variant="outline">{messages.filter(m => m.unread).length} unread</Badge>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Conversations List */}
                <Card className={`lg:col-span-1 ${cardClass}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Conversations</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-1 p-3">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                              selectedChatStudent?.senderId === msg.senderId 
                                ? 'bg-primary/10 border border-primary/30' 
                                : msg.unread ? 'bg-primary/5' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => setSelectedChatStudent(msg)}
                          >
                            <Avatar className="w-10 h-10">
                              <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-sm">
                                {msg.from.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">{msg.from}</p>
                                {msg.unread && <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />}
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{msg.message}</p>
                            </div>
                            <span className="text-xs text-muted-foreground">{msg.time}</span>
                          </div>
                        ))}
                        {messages.length === 0 && (
                          <div className="text-center py-8 text-muted-foreground">
                            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">No conversations yet</p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Chat Area */}
                <Card className={`lg:col-span-2 ${cardClass}`}>
                  {selectedChatStudent ? (
                    <>
                      {/* Chat Header */}
                      <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white">
                              {selectedChatStudent.from.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <CardTitle className="text-lg">{selectedChatStudent.from}</CardTitle>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                              Student
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {/* Chat Messages */}
                      <CardContent className="p-0">
                        <ScrollArea className="h-[350px] p-4">
                          <div className="space-y-4">
                            {getConversationWithStudent(selectedChatStudent.senderId).map((msg, index) => {
                              const isMe = msg.sender_id === user?.user_id;
                              return (
                                <motion.div
                                  key={msg.message_id || index}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div className={`flex gap-2 max-w-[80%] ${isMe ? 'flex-row-reverse' : ''}`}>
                                    <Avatar className="w-8 h-8 flex-shrink-0">
                                      <AvatarFallback className={`text-xs ${
                                        isMe 
                                          ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                                          : 'bg-gradient-to-r from-emerald-500 to-cyan-500'
                                      } text-white`}>
                                        {isMe ? 'ME' : msg.sender_name?.split(' ').map(n => n[0]).join('')}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className={`rounded-2xl px-4 py-2 ${
                                      isMe 
                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                                        : 'bg-muted'
                                    }`}>
                                      <p className="text-sm">{msg.content}</p>
                                      <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-muted-foreground'}`}>
                                        {formatTimeAgo(msg.created_at)}
                                      </p>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                            <div ref={chatEndRef} />
                          </div>
                        </ScrollArea>
                        
                        {/* Message Input */}
                        <div className="border-t p-4">
                          <div className="flex gap-3">
                            <Textarea
                              value={chatMessage}
                              onChange={(e) => setChatMessage(e.target.value)}
                              placeholder={`Message ${selectedChatStudent.from}...`}
                              className="min-h-[44px] max-h-[120px] resize-none"
                              rows={1}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendChatMessage();
                                }
                              }}
                            />
                            <Button 
                              onClick={handleSendChatMessage}
                              disabled={!chatMessage.trim()}
                              className="bg-gradient-to-r from-purple-500 to-pink-500 px-6"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </>
                  ) : (
                    <CardContent className="h-[500px] flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p className="text-lg font-medium mb-2">Select a conversation</p>
                        <p className="text-sm">Choose a student from the list to start chatting</p>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">My Schedule</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle className="text-lg">Today</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {schedule.filter(s => s.day === 'Today').map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                          <div className={`w-2 h-12 rounded-full ${item.type === 'class' ? 'bg-blue-500' : 'bg-orange-500'}`} />
                          <div className="flex-1">
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-muted-foreground">{item.time}</p>
                          </div>
                          <Button size="sm">
                            <Play className="w-3 h-3 mr-1" /> Start
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle className="text-lg">Tomorrow</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {schedule.filter(s => s.day === 'Tomorrow').map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                          <div className={`w-2 h-12 rounded-full ${item.type === 'class' ? 'bg-blue-500' : 'bg-orange-500'}`} />
                          <div className="flex-1">
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-muted-foreground">{item.time}</p>
                          </div>
                          <Badge variant="outline">{item.type}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Annotation Modal */}
      {annotatingSubmission && (
        <AnnotationTool
          submission={annotatingSubmission}
          onClose={() => setAnnotatingSubmission(null)}
          onSave={handleSaveAnnotation}
        />
      )}

      {/* Full View Modal */}
      {fullViewSubmission && (
        <FullViewModal
          submission={fullViewSubmission}
          onClose={() => setFullViewSubmission(null)}
        />
      )}

      {/* Annotation Preview Viewer - Shows mentor what they submitted */}
      {viewingAnnotations && (
        <AnnotationPreviewViewer
          submission={viewingAnnotations}
          onClose={() => setViewingAnnotations(null)}
        />
      )}

      {/* Student Detail Modal */}
      {selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}

      {/* Reply Message Dialog */}
      {replyingTo && (
        <Dialog open={!!replyingTo} onOpenChange={() => setReplyingTo(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Reply to {replyingTo.from}
              </DialogTitle>
              <DialogDescription>
                Original message: "{replyingTo.message.substring(0, 100)}{replyingTo.message.length > 100 ? '...' : ''}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows={4}
                className="resize-none"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</Button>
              <Button onClick={handleSendReply} className="bg-gradient-to-r from-emerald-500 to-cyan-500">
                <Send className="w-4 h-4 mr-2" />
                Send Reply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Mentor Profile Modal */}
      {showProfileModal && (
        <MentorProfileModal 
          user={user}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}
